import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { MilestoneProgressStatus } from '../../shared/enums/milestone-progress-status';
import { ICreateMilestoneRequestDto } from '../../shared/models/requests/create-milestone.request';
import { IReviewRequestDto } from '../../shared/models/requests/review.request';
import { IMilestoneResponseDto } from '../../shared/models/responses/milestone.response';
import { IReviewResponseDto } from '../../shared/models/responses/review.response';
import { environment } from '../../../environments/environment';
import { JobPayment } from '../../jobs/models/job-payment';
import { ProfileInfo } from '../../shared/models/profile-info';
import { UiStateStore } from '../../shared/store/ui-state-store.service';
import { JobInfo } from '../../jobs/models/job-info';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  constructor(private httpClient: HttpClient, private uiState: UiStateStore) { }

  inviteCandidate(accountId: string, jobId: string): Observable<void> {
    const url = `${environment.baseApiUrl}/candidates/${accountId}/invite/${jobId}`;
    return this.httpClient.get(url)
    .pipe(map(() => { return; }),
      catchError(err => {
        this.uiState.setError(`Error when inviting a candidate: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  hireCandidate(accountId: string, jobId: string): Observable<void> {
    const url = `${environment.baseApiUrl}/candidates/${accountId}/hire/${jobId}`;
    return this.httpClient.get(url)
    .pipe(map(() => { return; }),
      catchError(err => {
        this.uiState.setError(`Error when hiring a candidate: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  finishCandidateJob(jobId: string): Observable<void> {
    const url = `${environment.baseApiUrl}/jobs/finish/${jobId}`;
    return this.httpClient.get(url)
    .pipe(map(() => { return; }),
      catchError(err => {
        this.uiState.setError(`Error when finishing job: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  getProfileInfo(accountId: string): Observable<ProfileInfo> {
    const url = `${environment.baseApiUrl}/profiles/info/${accountId}`;
    return this.httpClient.get<ProfileInfo>(url)
      .pipe(map(response => {
        return ProfileInfo.parse(response);
      }),
        catchError(err => {
          this.uiState.setError(`Error when requesting profileinfo: ${err["message"]}`);
          return throwError(err);
        })
      );
  }

  getBusinessInfo(accountId: string): Observable<ProfileInfo> {
    const url = `${environment.baseApiUrl}/business/info/${accountId}`;
    return this.httpClient.get<ProfileInfo>(url)
      .pipe(map(response => {
        return ProfileInfo.parse(response);
      }),
        catchError(err => {
          this.uiState.setError(`Error when requesting bussiness profileinfo: ${err["message"]}`);
          return throwError(err);
        })
      );
  }

  getProfileInfos(jobId: string): Observable<ProfileInfo[]> {
    const url = `${environment.baseApiUrl}/jobs/${jobId}/candidates`;
    return this.httpClient.get<ProfileInfo[]>(url)
      .pipe(map(response => {
        return response.map(resp => ProfileInfo.parse(resp));
      }),
        catchError(err => {
          this.uiState.setError(`Error when requesting job applicant profileinfos: ${err["message"]}`);
          return throwError(err);
        })
      );
  }

  savePayment(jobPayment: JobPayment): Observable<JobPayment> {
    const url = `${environment.baseApiUrl}/jobpayment`;
    return this.httpClient.post<JobPayment>(url, jobPayment)
      .pipe(map(response => {
        return response as JobPayment;
      }),
        catchError(err => {
          this.uiState.setError(`Error when saving job payment: ${err["message"]}`);
          return throwError(err);
        }));
  }

  saveMilestones = (milestones: ICreateMilestoneRequestDto[]) => {
    const url = `${environment.baseApiUrl}/jobmilestones`;
    return this.httpClient.post<IMilestoneResponseDto[]>(url, milestones)
      .pipe(map(response => {
        return response as IMilestoneResponseDto[];
      }),
        catchError(err => {
          this.uiState.setError(`Error when saving job milestones: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getJobMilestones = (jobId: string, candidateId: string): Observable<IMilestoneResponseDto[]> => {
    const url = `${environment.baseApiUrl}/jobmilestones/${jobId}/${candidateId}`;
    return this.httpClient.get<IMilestoneResponseDto[]>(url)
      .pipe(map(response => {
        return response as IMilestoneResponseDto[];
      }),
        catchError(err => {
          this.uiState.setError(`Error when fetching job milestones: ${err["message"]}`);
          return throwError(err);
        }));
  }

  deleteMilestone = (milestoneId: string): Observable<boolean> => {
    const url = `${environment.baseApiUrl}/jobmilestones/${milestoneId}`;
    return this.httpClient.delete<boolean>(url)
      .pipe(map(response => {
        return response as boolean;
      }),
        catchError(err => {
          this.uiState.setError(`Error when deleteing a milestone: ${err["message"]}`);
          return throwError(err);
        }));
  }

  updateMilestoneStatus = (milestoneId: string, progressStatusId: MilestoneProgressStatus): Observable<IMilestoneResponseDto[]> => {
    const url = `${environment.baseApiUrl}/jobmilestones/${milestoneId}/progress-status/${progressStatusId}`;
    return this.httpClient.get<IMilestoneResponseDto[]>(url)
      .pipe(map(response => {
        return response as IMilestoneResponseDto[];
      }),
        catchError(err => {
          this.uiState.setError(`Error when updating milestone progress status: ${err["message"]}`);
          return throwError(err);
        }));
  }

  submitReview = (request: IReviewRequestDto) => {
    const url = `${environment.baseApiUrl}/jobs/submit-review`;
    return this.httpClient.post<IReviewResponseDto>(url, request)
      .pipe(map(response => {
        return response as IReviewResponseDto;
      }),
        catchError(err => {
          this.uiState.setError(`Error when submitting review: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getJobReview = (jobId: string): Observable<IReviewResponseDto[]> => {
    const url = `${environment.baseApiUrl}/jobs/${jobId}/reviews`;
    return this.httpClient.get<IReviewResponseDto[]>(url)
      .pipe(map(response => {
        return response as IReviewResponseDto[];
      }),
        catchError(err => {
          this.uiState.setError(`Error when fetching job reviews: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getJobInfo(jobId: string | null): Observable<JobInfo> {
    const url = `${environment.baseApiUrl}/jobs/jobInfo/${jobId}`;
    return this.httpClient.get<JobInfo>(url).pipe(
      map((response) => {
        return JobInfo.parse(response);
      }),
      catchError((err) => {
        this.uiState.setError(`Error when getting job info: ${err['message']}`);
        return throwError(err);
      })
    );
  }

}
